/**
 * revisionProgramViewModel — pure `(reduxState, programId) → page props` for the revision-program detail.
 *
 * No hooks, no navigation, no API calls. The Screen does:
 *   `const vm = useSelector((state) => revisionProgramViewModel(state, programId))`
 *
 * Parent path is cache-only: `studyPrograms` + `formData.user.current_study_programs`
 * (no backoffice `getStudyProgram` — that endpoint is `b_access` only).
 * Day grouping + section titles live here (not in a formatter / not in the DS list).
 */
import moment from "moment"

import {
    sameStudyProgramId,
    toUIRevisionPrograms,
    type ApiStudyProgram,
} from "../../../adapters/parent/RevisionProgram/revisionProgramAdapter"
import { formatDayDotDate } from "../../../ui/formatters/dateFormatter"
import { type UIRevisionProgram, type UIRevisionProgramGroup } from "../../../ui/types"

export type RevisionProgramViewModel = {
    groups: UIRevisionProgramGroup[]
}

function findProgram(
    list: ApiStudyProgram[] | null | undefined,
    programId: string | number | null | undefined
): ApiStudyProgram | undefined {
    return (list ?? []).find((p) => sameStudyProgramId(p?.id, programId))
}

/** Legacy fallback when `start_date` is missing — mirrors `DaySectionComponent`. */
function dayFallbackTitle(day: number): string {
    const weekNumber = Math.ceil(day / 7)
    return `Jour ${day} (Semaine ${weekNumber})`
}

/**
 * Group items by calendar day when `date` is set, else by program `day` index
 * (so missing `start_date` still renders sections — no silent drop).
 *
 * @param dayTitle the title of a dated section — « Lundi • 06/07/2026 » by default, « Lundi 26 octobre »
 *        for a stage week.
 */
export function groupRevisionProgramsByDay(
    programs: UIRevisionProgram[],
    dayTitle: (iso: string) => string = formatDayDotDate
): UIRevisionProgramGroup[] {
    type Bucket = { id: string; title: string; sortKey: number; programs: UIRevisionProgram[] }
    const buckets: Record<string, Bucket> = {}

    for (const program of programs) {
        if (program.date != null) {
            const id = moment(program.date).format("YYYY-MM-DD")
            const sortKey = moment(program.date).valueOf()
            ;(buckets[id] ??= {
                id,
                title: dayTitle(program.date),
                sortKey,
                programs: [],
            }).programs.push(program)
            continue
        }

        if (program.day == null) continue
        const id = `day-${program.day}`
        ;(buckets[id] ??= {
            id,
            title: dayFallbackTitle(program.day),
            sortKey: program.day,
            programs: [],
        }).programs.push(program)
    }

    return Object.values(buckets)
        .sort((a, b) => a.sortKey - b.sortKey)
        .map(({ id, title, programs: items }) => ({ id, title, programs: items }))
}

/** Redux root is still untyped in this app — keep the VM boundary explicit. */
export function revisionProgramViewModel(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    state: any,
    programId: string | number | null | undefined
): RevisionProgramViewModel {
    const fromList = findProgram(state?.data?.studyPrograms?.studyPrograms, programId)
    const fromSubscribe = findProgram(state?.data?.formData?.user?.current_study_programs, programId)
    const program = fromList ?? fromSubscribe

    return {
        groups: groupRevisionProgramsByDay(toUIRevisionPrograms(program)),
    }
}
